// src/pages/merchant/Invoices.js
// Factures du marchand (téléchargement PDF)
// Maxula

import React, { useEffect, useState } from 'react';
import { FiDownload, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { invoiceAPI } from '../../services/api';

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR');

export const downloadInvoice = async (inv) => {
  try {
    const res = await invoiceAPI.downloadPdf(inv._id);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${inv.invoiceNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('Erreur lors du téléchargement du PDF');
  }
};

const InvoicesTable = ({ invoices }) => (
  <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 text-gray-600">
        <tr>
          <th className="px-4 py-3 text-left">N° Facture</th>
          <th className="px-4 py-3 text-left">Date</th>
          <th className="px-4 py-3 text-left">Type</th>
          <th className="px-4 py-3 text-right">Total TTC</th>
          <th className="px-4 py-3 text-center">Statut</th>
          <th className="px-4 py-3 text-right">PDF</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {invoices.map((inv) => (
          <tr key={inv._id} className="hover:bg-gray-50">
            <td className="px-4 py-3 font-medium text-gray-900 inline-flex items-center gap-2"><FiFileText className="text-gray-400" /> {inv.invoiceNumber}</td>
            <td className="px-4 py-3 text-gray-500">{new Date(inv.issuedAt).toLocaleDateString('fr-FR')}</td>
            <td className="px-4 py-3 capitalize">{inv.type}</td>
            <td className="px-4 py-3 text-right font-semibold">{fmt(inv.totalTTC)} MAD</td>
            <td className="px-4 py-3 text-center"><span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">{inv.status}</span></td>
            <td className="px-4 py-3 text-right">
              <button onClick={() => downloadInvoice(inv)} className="p-2 text-primary-600 hover:bg-primary-50 rounded inline-flex items-center gap-1">
                <FiDownload size={16} />
              </button>
            </td>
          </tr>
        ))}
        {invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Aucune facture.</td></tr>}
      </tbody>
    </table>
  </div>
);

const MerchantInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comLoading, setComLoading] = useState(false);
  const defaultMonth = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); })();
  const [comMonth, setComMonth] = useState(defaultMonth);
  useEffect(() => {
    (async () => {
      try { const res = await invoiceAPI.getMine(); setInvoices(res.data?.data || []); }
      catch (e) {} finally { setLoading(false); }
    })();
  }, []);

  // Facture de commission du mois choisi (persistée + numérotée côté serveur).
  const downloadCommission = async () => {
    setComLoading(true);
    try {
      const res = await invoiceAPI.commissionStatementBlob(comMonth ? { month: comMonth } : {});
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `facture-commission-${comMonth || 'apercu'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.status === 404
        ? `Aucune commission à facturer pour ${comMonth}.`
        : 'Facture de commission indisponible';
      toast.error(msg);
    } finally { setComLoading(false); }
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Mes factures</h1>
        <div className="flex items-center gap-2">
          <input type="month" value={comMonth} onChange={(e) => setComMonth(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200" />
          <button onClick={downloadCommission} disabled={comLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50">
            <FiFileText size={16} /> {comLoading ? 'Génération…' : 'Facture de commission'}
          </button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div> : <InvoicesTable invoices={invoices} />}
    </div>
  );
};

export { InvoicesTable };
export default MerchantInvoices;
